import { IBlogContentCode, IBlogVideo } from "types/interfaces/blog/IBlog";
import { PortableText } from "@portabletext/react";
import { sanityImgBuilder } from "../../sanity";
import {
  ContentBlockQuote,
  ContentCode,
  ContentH1,
  ContentH2,
  ContentH3,
  ContentH4,
  ContentImage,
  ContentImageWrapper,
  ContentInlineCode,
  ContentLink,
  ContentNormal,
  ContentOrderedList,
  ContentUnorderdList,
  ContentTableWrapper,
  ContentTableCaption,
  ContentTable,
  ContentTableHeader,
  ContentTableCell,
  ContentTableRow,
} from "./styles";

// Table component for rendering Sanity table content
const TableComponent = ({ value }: { value: any }) => {
  const { caption, columns, rows, isHeaderRow } = value;

  // If no columns or rows, don't render anything
  if (!columns || !rows || columns.length === 0 || rows.length === 0) {
    return null;
  }

  return (
    <ContentTableWrapper>
      {caption && <ContentTableCaption>{caption}</ContentTableCaption>}
      <ContentTable>
        <thead>
          <tr>
            {columns.map((column: string, index: number) => (
              <ContentTableHeader key={index}>{column}</ContentTableHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row: any, rowIndex: number) => (
            <ContentTableRow key={rowIndex} isEven={rowIndex % 2 === 0}>
              {row.cells.map((cell: string, cellIndex: number) => (
                <ContentTableCell key={cellIndex}>{cell}</ContentTableCell>
              ))}
            </ContentTableRow>
          ))}
        </tbody>
      </ContentTable>
    </ContentTableWrapper>
  );
};

interface IProps {
  content: any[];
}

const ArticleContent = ({ content }: IProps) => {
  return (
    <PortableText
      value={content}
      components={{
        block: {
          h1: ({ children }) => <ContentH1>{children}</ContentH1>,
          h2: ({ children }) => <ContentH2>{children}</ContentH2>,
          h3: ({ children }) => <ContentH3>{children}</ContentH3>,
          h4: ({ children }) => <ContentH4>{children}</ContentH4>,
          normal: ({ children }) => <ContentNormal>{children}</ContentNormal>,
          blockquote: ({ children }) => <ContentBlockQuote>{children}</ContentBlockQuote>,
        },
        list: {
          bullet: ({ children }) => <ContentUnorderdList>{children}</ContentUnorderdList>,
          number: ({ children }) => <ContentOrderedList>{children}</ContentOrderedList>,
        },
        types: {
          image: ({ value }) => (
            <ContentImageWrapper>
              <ContentImage src={sanityImgBuilder.image(value).url()} alt="content-image" />
            </ContentImageWrapper>
          ),
          video: ({ value }) => {
            const { url, caption, altText } = value as IBlogVideo;
            return <iframe height={"300"} width={"600"} src={url}></iframe>;
          },
          code: ({ value }) => {
            const { code, language } = value as IBlogContentCode;
            return (
              <ContentCode
                language={`language-${language === "sh" ? "bash" : language}`}
                content={`\n${code.trim()}`}
                enableCopy={true}
              />
            );
          },
          table: ({ value }) => <TableComponent value={value} />,
        },
        marks: {
          link: ({ children, value }) => <ContentLink href={value.href}>{children}</ContentLink>,
          code: ({ text }) => <ContentInlineCode>{text}</ContentInlineCode>,
        },
      }}
    />
  );
};

export default ArticleContent;
